// src/context/AuthContext.js

import React, { createContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    token: null,
    user_type: null,
    hasCompletedOnboarding: false,
  });

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    // Synchronously bootstrap from localStorage
    const storedToken = localStorage.getItem('authToken');
    const storedUserType = localStorage.getItem('userType');
    const storedOnboarding = localStorage.getItem('hasCompletedOnboarding') === 'true';
    if (storedToken) {
      setAuth({ token: storedToken, user_type: storedUserType, hasCompletedOnboarding: storedOnboarding });
    }
    // Then attempt silent refresh if expired, else mark initialized
    const refresh = async () => {
      if (!storedToken) {
        setInitialized(true);
        return;
      }
      try {
        const { exp } = jwtDecode(storedToken);
        const now = Date.now() / 1000;
        let token = storedToken;
        if (exp < now) {
          const resp = await axios.post('/api/myteconstruction/auth/refresh', null, { headers: { Authorization: `Bearer ${token}` } });
          token = resp.data.token;
          localStorage.setItem('authToken', token);
        }
        setAuth({ token, user_type: storedUserType, hasCompletedOnboarding: storedOnboarding });
      } catch (err) {
        console.warn('Silent token refresh failed:', err);
      } finally {
        setInitialized(true);
      }
    };
    refresh();
  }, []);

  const login = ({ token, user_type, hasCompletedOnboarding }) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('userType', user_type);
    localStorage.setItem('hasCompletedOnboarding', hasCompletedOnboarding);
    setAuth({ token, user_type, hasCompletedOnboarding });
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userType');
    localStorage.removeItem('hasCompletedOnboarding');
    setAuth({ token: null, user_type: null, hasCompletedOnboarding: false });
  };

  const completeOnboarding = () => {
    localStorage.setItem('hasCompletedOnboarding', 'true');
    setAuth((prev) => ({
      ...prev,
      hasCompletedOnboarding: true,
    }));
  };

  // Set up axios interceptors for auth headers and token refresh
  useEffect(() => {
    // Request interceptor: inject Bearer token
    const reqInterceptor = axios.interceptors.request.use(
      (config) => {
        if (auth.token) {
          config.headers.Authorization = `Bearer ${auth.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    // Response interceptor: on 401, attempt refresh and retry once
    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response && error.response.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          try {
            const resp = await axios.post(
              '/api/myteconstruction/auth/refresh',
              null,
              { headers: { Authorization: `Bearer ${auth.token}` } }
            );
            const newToken = resp.data.token;
            // Update auth context and storage
            localStorage.setItem('authToken', newToken);
            setAuth((prev) => ({ ...prev, token: newToken }));
            // Update header and retry original request
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (e) {
            logout();
            return Promise.reject(e);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, [auth.token]);
  // Prevent rendering until auth is initialized
  if (!initialized) return null;
  return (
    <AuthContext.Provider value={{ auth, login, logout, completeOnboarding }}>
      {children}
    </AuthContext.Provider>
  );
};
